import { useMemo } from 'react'

import { getHostedModuleCodeSnippetLanguage } from '@/core/config/getHostedModuleCodeSnippetLanguage'
import { getCodeSnippetLanguage } from '@/core/config/technologies'
import { MCP_TECHNOLOGY_ID } from '@/core/const/mcpGateway.const'
import { formatCodeSnippetCalls } from '@/core/utils/builder/formatCodeSnippetCalls.builder'

import { FALLBACK_INSTALLATION_TAB } from '../const'
import type { InstallationTab } from '../types'

interface UseInstallationTabsParams {
  setupCommandMap: Record<string, string>
  configurationMap: Record<string, string>
  importCode: string
  selectedTechnology?: string
  hostedRuntimeName?: string
}

export function useInstallationTabs({
  setupCommandMap,
  configurationMap,
  importCode,
  selectedTechnology,
  hostedRuntimeName,
}: UseInstallationTabsParams): InstallationTab[] {
  return useMemo(() => {
    const language =
      selectedTechnology === MCP_TECHNOLOGY_ID
        ? getHostedModuleCodeSnippetLanguage(hostedRuntimeName)
        : getCodeSnippetLanguage(selectedTechnology)
    const tabs: InstallationTab[] = []
    const formattedSetupCommandMap = Object.fromEntries(
      Object.entries(setupCommandMap).map(([key, value]) => [key, formatCodeSnippetCalls(value)])
    )
    const formattedConfigurationMap = Object.fromEntries(
      Object.entries(configurationMap).map(([key, value]) => [key, formatCodeSnippetCalls(value)])
    )
    const formattedImportCode = formatCodeSnippetCalls(importCode)

    if (selectedTechnology === MCP_TECHNOLOGY_ID) {
      const mcpClientConfig = formattedConfigurationMap['MCP client config']

      if (mcpClientConfig) {
        return [
          {
            label: 'Connect',
            language,
            codeMap: { 'MCP client config': mcpClientConfig },
          },
        ]
      }

      return [FALLBACK_INSTALLATION_TAB]
    }

    if (Object.keys(formattedSetupCommandMap).length > 0) {
      tabs.push({
        label: 'Installation',
        language: 'plainText',
        codeMap: formattedSetupCommandMap,
        format: Object.fromEntries(
          Object.entries(formattedSetupCommandMap).map(([key, value]) => [key, { terminal: value }])
        ),
      })
    }

    if (Object.keys(formattedConfigurationMap).length > 0) {
      const inCode = formattedConfigurationMap['In Code']
      const configFile = formattedConfigurationMap['Config file']
      const envVariable = formattedConfigurationMap['ENV variable']
      const txtConfig = formattedConfigurationMap.TXT
      const jsonConfig = formattedConfigurationMap.JSON
      const yamlConfig = formattedConfigurationMap.YAML
      const configurationCodeMap: Record<string, string> = {}
      const configurationFormat: Record<string, Record<string, string>> = {}

      if (inCode !== undefined) {
        configurationCodeMap['In code'] = inCode
        configurationFormat['In code'] = { terminal: inCode }
      }

      if (configFile !== undefined) {
        configurationCodeMap['Config file'] = configFile

        const configFileFormats: Record<string, string> = {}

        if (txtConfig !== undefined) {
          configFileFormats['.txt'] = txtConfig
        }

        if (jsonConfig !== undefined) {
          configFileFormats['.json'] = jsonConfig
        }

        if (yamlConfig !== undefined) {
          configFileFormats['.yaml'] = yamlConfig
        }

        if (Object.keys(configFileFormats).length > 0) {
          configurationFormat['Config file'] = configFileFormats
        }
      }

      if (envVariable !== undefined) {
        configurationCodeMap['ENV var'] = envVariable
        configurationFormat['ENV var'] = { terminal: envVariable }
      }

      if (Object.keys(configurationCodeMap).length > 0) {
        tabs.push({
          label: 'Configuration',
          language,
          codeMap: configurationCodeMap,
          format: Object.keys(configurationFormat).length > 0 ? configurationFormat : undefined,
          connectionStringGeneratorText: 'Generate connection string',
        })
      }
    }

    if (formattedImportCode) {
      tabs.push({
        label: 'Import',
        code: formattedImportCode,
        language,
        format: { terminal: formattedImportCode },
      })
    }

    return tabs.length > 0 ? tabs : [FALLBACK_INSTALLATION_TAB]
  }, [setupCommandMap, configurationMap, importCode, selectedTechnology, hostedRuntimeName])
}
